<template>
  <v-dialog v-model="visible" persistent max-width="1250">
    <v-card flat class="overflow-hidden im-card">
      <!-- HEADER -->
      <v-toolbar flat height="56" color="#37474f" dark class="flex-shrink-0">
        <v-icon class="mr-3">mdi-package-variant-plus</v-icon>
        <v-toolbar-title class="font-weight-black text-uppercase" style="letter-spacing:1px;font-size:0.9rem">
          Ingreso a Inventario
        </v-toolbar-title>
        <div class="ml-3 caption" style="opacity:0.7">Vincule los productos del proveedor con su catálogo interno</div>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <!-- BODY -->
      <v-card-text class="pa-0" :class="$vuetify.theme.dark ? 'bg-mapper-dark' : 'bg-mapper-light'">
        <!-- SECCIÓN ESTADO PROVEEDOR -->
        <v-sheet 
          class="pa-4 mx-4 mt-4 im-sheet d-flex align-center" 
          :color="$vuetify.theme.dark ? '#1e293b' : 'white'"
        >
          <v-avatar color="primary" size="48" class="mr-4">
            <v-icon dark>mdi-account-factory</v-icon>
          </v-avatar>
          <div>
            <div class="text-subtitle-1 font-weight-bold mb-0 text-title-pro" style="line-height:1.2">
              {{ proveedorXml.Nombre || 'Proveedor XML' }}
            </div>
            <div class="caption text-caption-pro">
              ID: {{ proveedorXml.Identificacion ? proveedorXml.Identificacion.Numero : '---' }}
            </div>
          </div>
          <v-spacer></v-spacer>
          
          <template v-if="localProveedor">
            <v-chip color="success" label outlined class="font-weight-bold">
              <v-icon left small>mdi-check-decagram</v-icon>
              Proveedor Registrado
            </v-chip>
          </template>
          <template v-else>
            <div class="d-flex align-center">
                <span class="caption orange--text font-weight-bold mr-3">NO ENCONTRADO EN EL SISTEMA</span>
                <v-btn 
                  color="orange darken-2" 
                  dark 
                  depressed
                  small 
                  style="border-radius:4px"
                  :loading="registeringSupplier"
                  @click="registerSupplier"
                >
                  Registrar Proveedor Ahora
                  <v-icon right small>mdi-plus-circle</v-icon>
                </v-btn>
              </div>
          </template>
        </v-sheet>

        <v-alert
          type="info"
          text
          tile
          class="mt-4 mb-0 custom-alert-pro"
          border="bottom"
        >
          Se han detectado {{ lines.length }} líneas en el documento. Los artículos marcados en azul ya han sido auto-vinculados.
        </v-alert>

        <v-data-table
          :headers="headers"
          :items="mappedLines"
          hide-default-footer
          :items-per-page="-1"
          class="mapper-table"
          fixed-header
          height="45vh"
          :item-class="rowClass"
        >
          <!-- COLUMNA PROVEEDOR -->
          <template v-slot:item.descripcion="{ item }">
            <div class="py-2">
              <div class="font-weight-bold text-body-2 text-title-pro">{{ item.descripcion }}</div>
              <div class="caption text-caption-pro">
                Cant: {{ item.cantidad }} {{ item.unidad }} | Cod: {{ item.articulo || item.cabys }}
              </div>
            </div>
          </template>

          <!-- COLUMNA ARTICULO INTERNO -->
          <template v-slot:item.internalArt="{ item }">
            <div class="d-flex align-center">
              <v-autocomplete
                v-model="item.internalArt"
                :items="articles"
                :loading="loadingArticles"
                item-text="nombre"
                return-object
                label="Vincular Artículo"
                outlined
                dense
                hide-details
                class="mt-2 mb-2 flex-grow-1 im-autocomplete"
                prepend-inner-icon="mdi-magnify"
                @change="onArticleSelected(item)"
                clearable
              >
                <template v-slot:selection="{ item }">
                  <v-chip small color="primary" outlined class="font-weight-bold">
                    {{ item.codigo }} - {{ item.nombre }}
                  </v-chip>
                </template>
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">{{ item.nombre }}</v-list-item-title>
                    <v-list-item-subtitle>{{ item.codigo }} | Stock: {{ item.existencia }} | Price: {{ item.monto }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
              <v-btn 
                icon 
                color="blue-grey darken-2" 
                class="ml-2 mt-2 mb-2" 
                @click="openQuickCreate(item)"
                title="Crear Artículo Nuevo"
              >
                <v-icon>mdi-plus-circle</v-icon>
              </v-btn>
            </div>
          </template>

          <!-- COLUMNA RESULTADO (CONVERSIÓN) -->
          <template v-slot:item.resultado="{ item }">
            <template v-if="item.internalArt">
              <div class="d-flex flex-column align-center justify-center py-2">
                <v-chip color="success lighten-5" text-color="success" small class="font-weight-black mb-1">
                  + {{ calculateTotalUnits(item) }} Unid.
                </v-chip>
                <div class="caption text-caption-pro text-center" style="line-height:1.1">
                  Fact: {{ item.internalArt.unidadesPorPaquete || 1 }}
                </div>
              </div>
            </template>
            <div v-else class="caption orange--text font-italic text-center">Sin vincular</div>
          </template>

          <!-- CABECERAS MANUALES PARA CONTROL TOTAL (EJE 20PX) -->
          <template v-slot:header.utilidad>
            <div class="header-sync-pro">UT %</div>
          </template>
          <template v-slot:header.precioFinal>
            <div class="header-sync-pro">P. FINAL</div>
          </template>
          <template v-slot:header.precioSinIva>
            <div class="header-sync-pro">BASE</div>
          </template>
          <template v-slot:header.costoActual>
            <div class="header-sync-pro">ACTUAL</div>
          </template>
          <template v-slot:header.nuevoCosto>
            <div class="header-sync-pro">NUEVO</div>
          </template>

          <!-- COLUMNAS FINANCIERAS (MAGNETISMO DINÁMICO) -->
          <template v-slot:item.utilidad="{ item }">
            <div class="cell-sync-pro success--text">
              {{ item.utilidad.toFixed(1) }}%
            </div>
          </template>

          <template v-slot:item.precioFinal="{ item }">
            <template v-if="item.internalArt">
               <div class="cell-sync-pro">
                  <div class="magnet-box-pro">
                    <span class="symbol-sync-pro">₡</span>
                    <input
                      v-model.number="item.nuevoPrecioFinal"
                      type="number"
                      class="input-dynamic-pro"
                      :style="{ width: ((item.nuevoPrecioFinal + '').length + 2) + 'ch' }"
                      @input="calculateFinancials(item, 'final')"
                    >
                  </div>
               </div>
            </template>
          </template>

          <template v-slot:item.precioSinIva="{ item }">
            <template v-if="item.internalArt">
               <div class="cell-sync-pro">
                  <div class="magnet-box-pro">
                    <span class="symbol-sync-pro">₡</span>
                    <input
                      v-model.number="item.nuevoPrecioSinIva"
                      type="number"
                      class="input-dynamic-pro"
                      :style="{ width: ((item.nuevoPrecioSinIva + '').length + 2) + 'ch' }"
                      @input="calculateFinancials(item, 'net')"
                    >
                  </div>
               </div>
            </template>
          </template>

          <template v-slot:item.costoActual="{ item }">
            <div class="cell-sync-pro font-weight-bold" style="color: #1e293b">
               ₡{{ (item.internalArt ? item.internalArt.costo : 0).toLocaleString() }}
            </div>
          </template>

          <template v-slot:item.nuevoCosto="{ item }">
            <template v-if="item.internalArt">
               <div class="cell-sync-pro">
                  <div class="magnet-box-pro">
                    <span class="symbol-sync-pro">₡</span>
                    <input
                      v-model.number="item.nuevoCosto"
                      type="number"
                      class="input-dynamic-pro"
                      :style="{ width: ((item.nuevoCosto + '').length + 2) + 'ch' }"
                      @input="calculateFinancials(item, 'cost')"
                    >
                  </div>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-icon
                        v-on="on"
                        :color="getCostArrowColor(item)"
                        x-small
                        class="ml-1"
                      >{{ getCostArrowIcon(item) }}</v-icon>
                    </template>
                    <span>{{ getCostArrowTooltip(item) }}</span>
                  </v-tooltip>
               </div>
            </template>
          </template>
        </v-data-table>

        <!-- INFO FOOTER PANEL -->
        <v-sheet 
          class="px-6 py-4 d-flex align-center border-top"
          :color="$vuetify.theme.dark ? '#1e293b' : 'white'"
        >
          <v-switch
            v-model="updateCatalog"
            color="success"
            class="mt-0"
            hide-details
            dense
          >
            <template v-slot:label>
              <span class="caption font-weight-bold text-caption-pro" style="line-height: 1.2">
                Actualizar mi catálogo de proveedores (Vincular SKU y Costo)
              </span>
            </template>
          </v-switch>
          <v-spacer></v-spacer>
          <div class="caption text-uppercase font-weight-bold text-caption-pro">
            Al procesar, se guardará el movimiento de inventario automáticamente.
          </div>
        </v-sheet>
      </v-card-text>

      <!-- FOOTER -->
      <v-divider></v-divider>
      <div class="im-footer d-flex align-center px-4 py-3" :class="$vuetify.theme.dark ? 'im-footer--dark' : ''">
        <v-btn text class="font-weight-bold im-btn" @click="$emit('close')">Cancelar</v-btn>
        <v-spacer></v-spacer>
        <v-btn 
          color="#37474f"
          dark
          depressed 
          class="px-10 font-weight-bold im-btn"
          :disabled="!isValid"
          :loading="processing"
          @click="confirmMapping"
        >
          <v-icon left>mdi-database-import</v-icon>
          Procesar Entrada
        </v-btn>
      </div>
    </v-card>

    <!-- MODAL REGISTRO RÁPIDO DE ARTÍCULO -->
    <v-dialog v-model="quickArticleDialog" persistent max-width="800px">
      <v-card flat class="overflow-hidden">
        <v-toolbar flat height="56" color="#37474f" dark>
          <v-icon class="mr-2">mdi-package-variant-plus</v-icon>
          <v-toolbar-title class="font-weight-black text-uppercase" style="letter-spacing:1px;font-size:0.9rem">Registro Rápido de Artículo</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="quickArticleDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>

        <v-card-text class="pa-8">
          <v-form ref="quickForm" v-model="quickValid">
            <v-row dense>
              <v-col cols="12" sm="4">
                <v-text-field v-model="quickItem.codigo" label="Código Sugerido" outlined dense hide-details readonly class="mb-3" prepend-inner-icon="mdi-identifier"></v-text-field>
              </v-col>
              <v-spacer></v-spacer>
              <v-col cols="12">
                <v-text-field v-model="quickItem.nombre" label="Descripción del Producto" outlined dense prepend-inner-icon="mdi-format-title" required :rules="[v => !!v || 'Requerido']" hide-details class="mb-3"></v-text-field>
              </v-col>
              <v-col cols="12" sm="9">
                <v-text-field v-model="quickItem.cabys" label="Código Cabys (Hacienda)" outlined dense hide-details readonly prepend-inner-icon="mdi-barcode-scan" required :rules="[v => !!v || 'Requerido']" class="mb-3"></v-text-field>
              </v-col>
              <v-col cols="12" sm="3">
                <v-btn color="#37474f" dark block depressed height="40" style="border-radius:4px" @click="dialogCabys = true">
                  <v-icon left size="18">mdi-magnify</v-icon> BUSCAR
                </v-btn>
              </v-col>
              <v-col cols="12" sm="4">
                <v-select v-model="quickItem.unidad" :items="unidades" item-text="un_nombre" item-value="un_codigo" label="Unidad de Medida" outlined dense hide-details prepend-inner-icon="mdi-scale" class="mb-3"></v-select>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field v-model="quickItem.unidadesPorPaquete" label="U. por Empaque" outlined dense hide-details type="number" prepend-inner-icon="mdi-numeric-1-box-multiple" class="mb-3"></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                 <v-select v-model="quickItem.tarifa" :items="tarifasIVA" label="Tarifa IVA" outlined dense hide-details prepend-inner-icon="mdi-percent" @change="calculateQuickPricing" class="mb-3"></v-select>
              </v-col>

              <v-col cols="12">
                <v-divider class="my-4"></v-divider>
                <div class="subtitle-2 primary--text font-weight-bold mb-3">Costos y Precios</div>
              </v-col>

              <v-col cols="12" sm="4">
                <v-text-field v-model="quickItem.costo" label="Costo del Empaque" outlined dense hide-details prepend-inner-icon="mdi-cash-minus" prefix="₡" type="number" @input="calculateQuickPricing" class="font-weight-bold mb-3"></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field v-model="quickItem.utilidad" label="Utilidad (%)" outlined dense hide-details prepend-inner-icon="mdi-trending-up" suffix="%" type="number" @input="updateQuickPriceFromUtility" class="mb-3"></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field v-model="quickItem.monto" label="Precio Venta Unidad" outlined dense hide-details prepend-inner-icon="mdi-cash-check" prefix="₡" type="number" class="font-weight-bold" color="success" @input="updateQuickUtilityFromPrice"></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>
        <div class="im-footer d-flex align-center px-4 py-3" :class="$vuetify.theme.dark ? 'im-footer--dark' : ''">
          <v-spacer></v-spacer>
          <v-btn text class="mr-3 font-weight-bold im-btn" @click="quickArticleDialog = false">Cancelar</v-btn>
          <v-btn color="#37474f" dark depressed class="px-10 font-weight-bold im-btn" @click="saveQuickArticle" :loading="savingQuick">
            <v-icon left>mdi-content-save</v-icon> Crear y Vincular
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- DIALOGO CABYS REUTILIZABLE -->
    <v-dialog v-model="dialogCabys" width="1000" persistent>
      <v-card flat class="overflow-hidden" style="border-radius:4px">
        <template v-if="dialogCabys">
          <CabysComponent
            :allwedOnly="true"
            :initial-query="quickItem.nombre"
            @clicked="selectedCabys"
            @closed="dialogCabys = false"
          />
        </template>
      </v-card>
    </v-dialog>

    <Snackbar v-bind:text="snackText" v-bind:snackbar.sync="snackValue" />
  </v-dialog>
</template>

<script>
import CabysComponent from "../Cabys.vue";
import Snackbar from "./Snackbar.vue";
import axios from "axios";

export default {
  components: {
    CabysComponent,
    Snackbar
  },
  props: {
    show: Boolean,
    lines: Array,
    cia: String,
    proveedorXml: Object,
    localProveedor: Object
  },
  data: () => ({
    articles: [],
    loadingArticles: false,
    registeringSupplier: false,
    updateCatalog: true,
    mappedLines: [],
    processing: false,
    headers: [
      { text: "XML (DESCRIPCIÓN PROVEEDOR)", value: "descripcion", sortable: false, width: "320" },
      { text: "Vincular Catálogo", value: "internalArt", sortable: false, width: "220" },
      { text: "STOCK", value: "resultado", sortable: false, align: "center", width: "85" },
      { text: "UT %", value: "utilidad", sortable: false, align: "right", width: "80" },
      { text: "P. FINAL", value: "precioFinal", sortable: false, align: "right", width: "110" },
      { text: "BASE", value: "precioSinIva", sortable: false, align: "right", width: "110" },
      { text: "ACTUAL", value: "costoActual", sortable: false, align: "right", width: "100" },
      { text: "NUEVO", value: "nuevoCosto", sortable: false, align: "right", width: "135" },
    ],

    // Registro Rápido
    quickArticleDialog: false,
    savingQuick: false,
    quickValid: false,
    dialogCabys: false,
    snackText: "",
    snackValue: false,
    unidades: [],
    tarifasIVA: [
      { text: "Exento (0%)", value: 0 },
      { text: "IVA 1%", value: 1 },
      { text: "IVA 2%", value: 2 },
      { text: "IVA 4%", value: 4 },
      { text: "IVA 13%", value: 13 },
    ],
    quickItem: {
      codigo: "",
      nombre: "",
      cabys: "",
      unidad: "Unid",
      tarifa: 13,
      costo: 0,
      utilidad: 30,
      monto: 0,
      unidadesPorPaquete: 1,
      currentLine: null
    },
    providerLinks: []
  }),

  computed: {
    visible: {
      get() { return this.show; },
      set(val) { if (!val) this.$emit('close'); }
    },
    isValid() {
      return this.mappedLines.some(l => l.internalArt);
    },
    unitCostResult() {
      const costo = parseFloat(this.quickItem.costo) || 0;
      const factor = parseInt(this.quickItem.unidadesPorPaquete) || 1;
      return (costo / factor).toFixed(2);
    }
  },

  watch: {
    show(val) {
      if (val) {
        this.getArticles();
        this.getProviderLinks();
        this.prepareMappedLines();
        this.fetchUnidades();
      }
    }
  },

  methods: {
    getArticles() {
      this.loadingArticles = true;
      axios.get("api/Articulos/getArticulosByCompany?cia=" + this.cia)
        .then(res => {
          this.articles = res.data;
          this.autoMatch();
        })
        .finally(() => { this.loadingArticles = false; });
    },

    fetchUnidades() {
      axios.get("api/Articulos/getUnidades").then(res => {
        this.unidades = res.data;
      });
    },

    getProviderLinks() {
      if (!this.proveedorXml || !this.proveedorXml.Identificacion || !this.proveedorXml.Identificacion.Numero) return;
      axios.get(`api/Articulos/getArticulosByProveedor?cia=${this.cia}&proveedorCodigo=${this.proveedorXml.Identificacion.Numero}`)
        .then(res => {
          this.providerLinks = res.data;
          this.autoMatch();
        });
    },

    prepareMappedLines() {
      this.mappedLines = this.lines.map(line => ({
        ...line,
        internalArt: null,
        nuevoCosto: 0,
        nuevoPrecioSinIva: 0,
        nuevoPrecioFinal: 0,
        utilidad: 0,
        ivaPorc: 0
      }));
    },

    autoMatch() {
      this.mappedLines.forEach(line => {
        // 1. Prioridad: Vínculo de Proveedor
        const supplierCode = (line.articulo || line.cabys || "").toString().trim();
        const link = this.providerLinks.find(l => 
          l.codigoInternoProveedor && 
          l.codigoInternoProveedor.toString().trim() === supplierCode
        );
        
        let match = null;
        if (link) {
          match = this.articles.find(a => a.codigo === link.codigoArticulo);
        }

        // 2. Respaldo: Nombre o Códigos Directos
        if (!match) {
          match = this.articles.find(a => 
            (a.nombre || "").toLowerCase().trim() === (line.descripcion || "").toLowerCase().trim() ||
            (a.barras || "").toString().trim() === supplierCode ||
            (a.codigo || "").toString().trim() === supplierCode
          );
        }

        if (match) {
          line.internalArt = match;
          this.onLinkArticle(line);
        }
      });
    },

    onArticleSelected(line) {
        if (line.internalArt) {
          const factor = (line.internalArt.unidadesPorPaquete || line.internalArt.UnidadesPorPaquete) || 1;
          
          // CORRECCIÓN: Usar line.monto que es como viene del backend
          const billingPrice = parseFloat(line.monto) || 0;
          line.nuevoCosto = billingPrice / factor;
          
          line.nuevoPrecioFinal = (line.internalArt.monto || line.internalArt.Monto) || 0;
          line.ivaPorc = (line.internalArt.tarifa || line.internalArt.Tarifa) || 0;
          
          this.calculateFinancials(line, 'final');
        }
    },

    onLinkArticle(line) {
        this.onArticleSelected(line);
    },

    calculateFinancials(line, source) {
      if (!line.internalArt) return;

      const currentNet = parseFloat(line.nuevoPrecioSinIva) || 0;
      const currentFinal = parseFloat(line.nuevoPrecioFinal) || 0;
      const currentCost = parseFloat(line.nuevoCosto) || 0;
      const iva = parseFloat(line.ivaPorc) || 0;

      if (source === 'net') {
        line.nuevoPrecioFinal = parseFloat((currentNet * (1 + (iva / 100))).toFixed(2));
      } else if (source === 'final') {
        line.nuevoPrecioSinIva = parseFloat((currentFinal / (1 + (iva / 100))).toFixed(2));
      }

      const finalNet = parseFloat(line.nuevoPrecioSinIva) || 0;
      if (finalNet > 0) {
        line.utilidad = ((finalNet - currentCost) / finalNet) * 100;
      } else {
        line.utilidad = 0;
      }
    },

    openQuickCreate(line) {
      this.quickItem.currentLine = line;
      this.quickItem.nombre = line.descripcion;
      this.quickItem.costo = line.monto;
      this.quickItem.cabys = line.cabys || "";
      this.quickItem.unidad = line.unidad || "Unid";
      this.quickItem.tarifa = parseFloat(line.impTarifa) || 13;
      this.quickItem.monto = 0;
      this.quickItem.utilidad = 0; 
      this.quickItem.unidadesPorPaquete = 1; // Default
      
      axios.get(`api/Articulos/getCodigoArticulo?cia=${this.cia}`).then(res => {
        this.quickItem.codigo = res.data;
        // Se eliminó calculateQuickPricing() para que inicie en 0
        this.quickArticleDialog = true;
      });
    },

    selectedCabys(cabys) {
      this.quickItem.cabys = cabys.codigo;
      this.dialogCabys = false;
    },

    calculateQuickPricing() {
      const packCosto = parseFloat(this.quickItem.costo) || 0;
      const factor = parseInt(this.quickItem.unidadesPorPaquete) || 1;
      const unitCosto = packCosto / factor;
      
      const utilidad = parseFloat(this.quickItem.utilidad) || 0;
      const tarifa = parseFloat(this.quickItem.tarifa) || 0;

      const precioNeto = unitCosto * (1 + utilidad / 100);
      const precioFinal = precioNeto * (1 + tarifa / 100);
      
      this.quickItem.monto = Math.round(precioFinal);
    },

    updateQuickPriceFromUtility() {
      this.calculateQuickPricing();
    },

    updateQuickUtilityFromPrice() {
      const monto = parseFloat(this.quickItem.monto) || 0;
      const packCosto = parseFloat(this.quickItem.costo) || 0;
      const factor = parseInt(this.quickItem.unidadesPorPaquete) || 1;
      const unitCosto = packCosto / factor;
      const tarifa = parseFloat(this.quickItem.tarifa) || 0;

      if (unitCosto > 0) {
        const precioNeto = monto / (1 + (tarifa / 100));
        const utilidad = ((precioNeto / unitCosto) - 1) * 100;
        this.quickItem.utilidad = Math.round(utilidad * 10) / 10;
      }
    },

    saveQuickArticle() {
      if (!this.$refs.quickForm.validate()) return;
      if (!this.quickItem.cabys) {
        this.snackText = "Debe buscar y seleccionar un código Cabys";
        this.snackValue = true;
        return;
      }

      this.savingQuick = true;
      
      const payload = {
        Cia: this.cia,
        Codigo: this.quickItem.codigo,
        Nombre: this.quickItem.nombre.toUpperCase(),
        TipoArt: "02",
        Unidad: this.quickItem.unidad,
        Tarifa: parseFloat(this.quickItem.tarifa) || 0,
        Monto: parseFloat(this.quickItem.monto) || 0,
        Costo: parseFloat(this.unitCostResult) || 0,
        Cabys: this.quickItem.cabys,
        AfectaInventario: true,
        Estado: "Activo",
        UtilidadMinima: 0,
        Existencia: 0, 
        ExistenciaMin: 0,
        ExistenciaMax: 0,
        SeCompraPor: this.quickItem.unidad,
        UnidadesPorPaquete: parseInt(this.quickItem.unidadesPorPaquete) || 1,
        CodigoImp: "01",
        CodigoTarifa: this.quickItem.tarifa == 13 ? "08" : "01"
      };

      axios.post("api/Articulos/insertArticulo", payload)
        .then(() => {
          this.snackText = "Artículo creado exitosamente";
          this.snackValue = true;
          this.quickArticleDialog = false;
          
          if (this.quickItem.currentLine) {
            // Sincronizamos las propiedades para que la tabla las reconozca (camelCase)
            const syncedArt = {
              codigo: payload.Codigo,
              nombre: payload.Nombre,
              cabys: payload.Cabys,
              unidad: payload.Unidad,
              monto: payload.Monto,
              costo: payload.Costo,
              unidadesPorPaquete: payload.UnidadesPorPaquete,
              existencia: 0
            };
            this.quickItem.currentLine.internalArt = syncedArt;
            this.onArticleSelected(this.quickItem.currentLine);
          }
          this.getArticles();
        })
        .catch(err => {
          console.error(err);
          const errorMsg = (err.response && err.response.data) ? err.response.data : err.message;
          this.snackText = "Error al crear artículo: " + errorMsg;
          this.snackValue = true;
        })
        .finally(() => {
          this.savingQuick = false;
        });
    },

    getUtilidadColor(line) {
      if (line.utilidad < 0) return 'red--text text--lighten-1';
      return 'green--text text--darken-1';
    },

    calculateTotalUnits(line) {
      if (!line.internalArt) return 0;
      const factor = (line.internalArt.unidadesPorPaquete || line.internalArt.UnidadesPorPaquete) || 1;
      return line.cantidad * factor;
    },

    confirmMapping() {
      const selectedMappings = this.mappedLines
        .filter(l => l.internalArt)
        .map(l => ({
          articuloCodigo: l.internalArt.codigo || l.internalArt.Codigo,
          articuloNombre: l.internalArt.nombre || l.internalArt.Nombre,
          articuloCabys: l.internalArt.cabys || l.internalArt.Cabys,
          cantidadXML: l.cantidad,
          cantidadInventario: this.calculateTotalUnits(l),
          costoUnitario: l.nuevoCosto,
          precioSinIva: l.nuevoPrecioFinal,
          lineaXML: l.numLinea,
          codigoInternoProveedor: l.articulo || l.cabys
        }));

      this.$emit('confirm', {
        mappings: selectedMappings,
        updateCatalog: this.updateCatalog,
        proveedorCodigo: this.localProveedor ? (this.localProveedor.codigo || this.localProveedor.Codigo) : null
      });
    },

    registerSupplier() {
      const p = this.proveedorXml;
      const nombreFinal = p.Nombre ? p.Nombre.trim() : "Proveedor Nuevo";
      const comercialFinal = (p.NombreComercial && p.NombreComercial.trim()) ? p.NombreComercial.trim() : nombreFinal;

      const payload = {
        Cia: this.cia,
        Nombre: nombreFinal,
        NombreComercial: comercialFinal,
        Cedula: p.Identificacion.Numero,
        TipoId: p.Identificacion.Tipo,
        Correo: p.CorreoElectronico,
        AreaTel: p.Telefono ? p.Telefono.Area : "506",
        Telefono: p.Telefono ? p.Telefono.Numero : "",
        Provincia: p.Ubicacion ? p.Ubicacion.Provincia : "1",
        Canton: p.Ubicacion ? p.Ubicacion.Canton : "01",
        Distrito: p.Ubicacion ? p.Ubicacion.Distrito : "01",
        Barrio: p.Ubicacion ? p.Ubicacion.Barrio : "01",
        Direccion: p.Ubicacion ? p.Ubicacion.OtrasSenas : "",
        Moneda: "CRC",
        EsReceptor: true
      };

      this.registeringSupplier = true;
      axios.post("api/Proveedores/insertProveedores", payload)
        .then(() => {
          this.$emit('supplier-registered', payload.Cedula);
        })
        .finally(() => {
          this.registeringSupplier = false;
        });
    },

    rowClass(item) {
      return item.internalArt ? 'row-linked' : '';
    },

    getCostArrowColor(item) {
      const nuevo = item.nuevoCosto || 0;
      const actual = (item.internalArt && item.internalArt.costo) || 0;
      if (nuevo > actual) return 'error';
      if (nuevo < actual) return 'success';
      return 'grey';
    },

    getCostArrowIcon(item) {
      const nuevo = item.nuevoCosto || 0;
      const actual = (item.internalArt && item.internalArt.costo) || 0;
      if (nuevo > actual) return 'mdi-arrow-up-thick';
      if (nuevo < actual) return 'mdi-arrow-down-thick';
      return 'mdi-equal';
    },

    getCostArrowTooltip(item) {
      const nuevo = item.nuevoCosto || 0;
      const actual = (item.internalArt && item.internalArt.costo) || 0;
      if (nuevo > actual) return 'Costo sube vs. actual';
      if (nuevo < actual) return 'Costo baja vs. actual';
      return 'Sin cambio de costo';
    }
  }
};
</script>

<style scoped>
/* === IM DESIGN SYSTEM (InventoryMapper) === */
.im-card {
  border-radius: 4px !important;
}
.im-btn {
  border-radius: 4px !important;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  font-size: 0.75rem !important;
}
.im-footer {
  background-color: #f8fafc;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
.im-footer--dark {
  background-color: #1e293b !important;
  border-top: 1px solid rgba(255, 255, 255, 0.07) !important;
}
.im-sheet {
  border: 1px solid #e2e8f0 !important;
  border-radius: 4px !important;
}
.theme--dark .im-sheet {
  border: 1px solid #334155 !important;
}
.im-autocomplete >>> .v-input__slot {
  border-radius: 4px !important;
}

/* Clases de utilidad con soporte de tema */
.bg-mapper-light { background-color: #f8fafc !important; }
.bg-mapper-dark { background-color: #0f172a !important; }

.text-title-pro { color: #1e293b !important; }
.theme--dark .text-title-pro { color: #f1f5f9 !important; }

.text-caption-pro { color: #64748b !important; }
.theme--dark .text-caption-pro { color: #94a3b8 !important; }

/* TABLA MAESTRA */
.mapper-table {
  background-color: transparent !important;
}

.mapper-table >>> thead th {
  background-color: #37474f !important;
  color: #ffffff !important;
  text-transform: uppercase !important;
  font-size: 0.65rem !important;
  font-weight: 700 !important;
  border-bottom: none !important;
  padding: 0 !important;
  height: 40px !important;
  text-align: right !important;
}
.mapper-table >>> .v-data-table-header__icon {
  display: none !important;
}
.mapper-table >>> .v-data-table-header__wrapper {
  padding: 0 15px !important;
  display: block !important;
}
.mapper-table >>> thead th {
  padding: 0 !important;
  height: 48px !important;
}
.mapper-table >>> td {
  padding: 0 !important;
  height: 60px !important;
}
.mapper-table >>> thead th:first-child,
.mapper-table >>> td:first-child {
  padding-left: 20px !important;
  text-align: left !important;
}
.theme--dark .mapper-table >>> thead th {
  background-color: #263238 !important;
  color: #cfd8dc !important;
}
.mapper-table >>> tbody tr:hover {
  background-color: rgba(55, 71, 79, 0.04) !important;
}

.header-sync-pro {
  text-align: right !important;
  padding-right: 20px !important;
  width: 100% !important;
}

.header-diamond-pro {
  text-align: right !important;
  width: 100% !important;
}

.cell-sync-pro {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-end !important;
  padding-right: 20px !important;
  width: 100% !important;
  height: 100% !important;
  font-size: 0.85rem !important;
  font-weight: 800 !important;
}

/* IMÁN DINÁMICO (INPUT AUTO-AJUSTABLE) */
.magnet-box-pro {
  display: inline-flex !important;
  align-items: center !important;
  border-bottom: 2px solid #cfd8dc;
  transition: border-color 0.2s;
  padding-bottom: 2px;
}
.magnet-box-pro:hover, .magnet-box-pro:focus-within {
  border-bottom-color: #37474f !important;
}

.symbol-sync-pro {
  font-size: 0.85rem !important;
  font-weight: 800 !important;
  color: #1e293b !important;
  margin-right: 2px !important;
}

.input-dynamic-pro {
  border: none !important;
  outline: none !important;
  background: transparent !important;
  text-align: right !important;
  font-family: 'Inter', sans-serif !important;
  font-size: 0.85rem !important;
  font-weight: 800 !important;
  color: #1e293b !important;
  padding: 0 !important;
  min-width: 25px !important;
}

.theme--dark .symbol-sync-pro,
.theme--dark .input-dynamic-pro {
  color: #f1f5f9 !important;
}

.custom-alert-pro {
  background-color: #eceff1 !important;
  border: 1px solid #cfd8dc !important;
  color: #37474f !important;
}

.row-linked {
  background-color: rgba(55, 71, 79, 0.04) !important;
}
</style>
